import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type ChargeCostDefinition,
  type CreateChargeCostDefinitionRequest,
  type UpdateChargeCostDefinitionRequest,
} from '../client';

/**
 * Stable query-key factory for charge/cost-definition-scoped caches. Same
 * shape as `serviceTagKeys` and `cargoDefinitionKeys` so a future suite-
 * wide tweak (e.g. global stale time) can iterate keys with the same
 * prefix.
 */
export const chargeCostDefinitionKeys = {
  all: ['tms', 'charge-cost-definition'] as const,
  byOrganisation: (organisationId: string) =>
    ['tms', 'charge-cost-definition', 'org', organisationId] as const,
};

/**
 * Fetch every charge / cost definition for an organisation. Pass `null`
 * to keep the query disabled (e.g. while the org id is still resolving
 * from Auth0).
 */
export function useChargeCostDefinitions(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<ChargeCostDefinition[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<ChargeCostDefinition[], Error>({
    queryKey: organisationId
      ? chargeCostDefinitionKeys.byOrganisation(organisationId)
      : chargeCostDefinitionKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getChargeCostDefinitions(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/tms/api/v1/chargeCostDefinitions`.
 * Invalidates the per-org list cache on success so the new row shows up.
 */
export function useCreateChargeCostDefinition() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateChargeCostDefinitionRequest>({
    mutationKey: [...chargeCostDefinitionKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createChargeCostDefinition(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: chargeCostDefinitionKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/tms/api/v1/chargeCostDefinitions`. The
 * caller passes `organisationId` alongside the body so we can target the
 * per-org list cache without an extra round-trip — same pattern as the
 * other update hooks in this package.
 */
export function useUpdateChargeCostDefinition() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; body: UpdateChargeCostDefinitionRequest }>({
    mutationKey: [...chargeCostDefinitionKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateChargeCostDefinition(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: chargeCostDefinitionKeys.byOrganisation(organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around DELETE
 * `/tms/api/v1/chargeCostDefinitions/{id}`. Soft-deletes on the BE — the
 * row stays in the DB with `IsDeleted=true` but disappears from the
 * per-org list via the global query filter. BE returns 409 Conflict for
 * `isProtected` rows.
 */
export function useDeleteChargeCostDefinition() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...chargeCostDefinitionKeys.all, 'delete'],
    mutationFn: ({ id }) => tmsApi.deleteChargeCostDefinition(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: chargeCostDefinitionKeys.byOrganisation(organisationId),
      });
    },
  });
}
